import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getSupabase } from "../lib/supabaseClient";

const FULFILLMENT = [
  { value: "new_order", label: "New order" },
  { value: "preparing", label: "Preparing" },
  { value: "ready_to_dispatch", label: "Ready to dispatch" },
  { value: "dispatched", label: "Dispatched" },
  { value: "completed", label: "Completed" },
  { value: "cancelled_refunded", label: "Cancelled / Refunded" },
];

const COURIERS = [
  { label: "Royal Mail", href: "https://www.royalmail.com" },
  { label: "Evri", href: "https://www.evri.com" },
  { label: "Yodel", href: "https://www.yodel.co.uk" },
];

function formatAddress(addr) {
  if (!addr || typeof addr !== "object") return "";
  const parts = [addr.line1, addr.line2, addr.city, addr.state, addr.postal_code, addr.country].filter(Boolean);
  return parts.join("\n");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function openPackingSlip(order) {
  const addr = formatAddress(order.shipping_address);
  const lines = (order.order_items || [])
    .map(
      (li) => `
      <tr>
        <td style="padding:8px;border:1px solid #ccc;">${escapeHtml(li.product_name)}</td>
        <td style="padding:8px;border:1px solid #ccc;">${escapeHtml(li.sku || "—")}</td>
        <td style="padding:8px;border:1px solid #ccc;">${escapeHtml(li.packaging_type || "—")}</td>
        <td style="padding:8px;border:1px solid #ccc;">${escapeHtml(li.base_type)}</td>
        <td style="padding:8px;border:1px solid #ccc;">${li.size_ml} ml</td>
        <td style="padding:8px;border:1px solid #ccc;">${escapeHtml(JSON.stringify(li.herbs))}</td>
        <td style="padding:8px;border:1px solid #ccc;">${li.quantity}</td>
      </tr>`,
    )
    .join("");
  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>Packing slip</title></head><body style="font-family:system-ui;padding:24px;">
    <h1 style="font-size:18px;">MossMatrix packing slip</h1>
    <p style="font-size:12px;color:#444;">Order ID: ${escapeHtml(order.id)}</p>
    <p style="font-size:12px;"><strong>Customer</strong><br/>${escapeHtml(order.customer_name || "—")}<br/>${escapeHtml(order.customer_email || "—")}<br/>${escapeHtml(order.customer_phone || "")}</p>
    <p style="font-size:12px;white-space:pre-wrap;"><strong>Ship to</strong><br/>${escapeHtml(addr || "—")}</p>
    <p style="font-size:12px;"><strong>Delivery</strong> ${escapeHtml(order.delivery_option || "—")} · <strong>Payment</strong> ${escapeHtml(order.status)}</p>
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:16px;">
      <thead><tr><th style="text-align:left;padding:8px;border:1px solid #ccc;">Product</th><th style="padding:8px;border:1px solid #ccc;">SKU</th><th style="padding:8px;border:1px solid #ccc;">Pack</th><th style="padding:8px;border:1px solid #ccc;">Base</th><th style="padding:8px;border:1px solid #ccc;">Size</th><th style="padding:8px;border:1px solid #ccc;">Herbs</th><th style="padding:8px;border:1px solid #ccc;">Qty</th></tr></thead>
      <tbody>${lines}</tbody>
    </table>
    <script>window.onload=function(){window.print();}</script>
  </body></html>`);
  w.document.close();
}

export default function AdminPage() {
  const supabase = useMemo(() => getSupabase(), []);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminTab, setAdminTab] = useState(/** @type {'orders'|'community_notes'} */ ("orders"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersError, setOrdersError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [trackingDraft, setTrackingDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [communityNotes, setCommunityNotes] = useState([]);
  const [communityNotesError, setCommunityNotesError] = useState(null);
  const [communityNotesLoading, setCommunityNotesLoading] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => sub.subscription.unsubscribe();
  }, [supabase]);

  const loadOrders = useCallback(async () => {
    if (!supabase || !session) return;
    setOrdersError(null);
    const { data, error } = await supabase
      .from("orders")
      .select("*, order_items(*)")
      .order("created_at", { ascending: false });
    if (error) {
      setOrdersError(error.message);
      setOrders([]);
      return;
    }
    setOrders(data || []);
  }, [supabase, session]);

  useEffect(() => {
    if (session) loadOrders();
  }, [session, loadOrders]);

  const loadCommunityNotes = useCallback(async () => {
    if (!supabase || !session) return;
    setCommunityNotesLoading(true);
    setCommunityNotesError(null);
    const { data, error } = await supabase
      .from("ingredient_notes")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      setCommunityNotesError(error.message);
      setCommunityNotes([]);
    } else {
      setCommunityNotes(data || []);
    }
    setCommunityNotesLoading(false);
  }, [supabase, session]);

  useEffect(() => {
    if (session && adminTab === "community_notes") loadCommunityNotes();
  }, [session, adminTab, loadCommunityNotes]);

  const approveCommunityNote = async (id) => {
    if (!supabase) return;
    setCommunityNotesError(null);
    const { error } = await supabase
      .from("ingredient_notes")
      .update({ status: "approved", approved_at: new Date().toISOString(), moderation_reason: null })
      .eq("id", id);
    if (error) setCommunityNotesError(error.message);
    else await loadCommunityNotes();
  };

  const rejectCommunityNote = async (id) => {
    if (!supabase) return;
    setCommunityNotesError(null);
    const { error } = await supabase
      .from("ingredient_notes")
      .update({ status: "rejected", moderation_reason: "Rejected by staff" })
      .eq("id", id);
    if (error) setCommunityNotesError(error.message);
    else await loadCommunityNotes();
  };

  const deleteCommunityNote = async (id) => {
    if (!supabase) return;
    if (!window.confirm("Delete this note permanently?")) return;
    setCommunityNotesError(null);
    const { error } = await supabase.from("ingredient_notes").delete().eq("id", id);
    if (error) setCommunityNotesError(error.message);
    else await loadCommunityNotes();
  };

  const selected = orders.find((o) => o.id === selectedId);

  useEffect(() => {
    if (!selected) {
      setTrackingDraft("");
      setNotesDraft("");
      return;
    }
    setTrackingDraft(selected.tracking_number || "");
    setNotesDraft(selected.internal_notes || "");
  }, [selected]);

  const signIn = async (e) => {
    e.preventDefault();
    setAuthError(null);
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setAuthError(error.message);
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setOrders([]);
    setSelectedId(null);
  };

  const patchOrder = async (orderId, patch) => {
    if (!supabase) return;
    const { error } = await supabase.from("orders").update(patch).eq("id", orderId);
    if (error) {
      setOrdersError(error.message);
      return;
    }
    await loadOrders();
  };

  const setFulfillment = async (orderId, fulfillment_status) => {
    const shipMap = {
      new_order: "processing",
      preparing: "processing",
      ready_to_dispatch: "processing",
      dispatched: "shipped",
      completed: "completed",
      cancelled_refunded: "unfulfilled",
    };
    await patchOrder(orderId, {
      fulfillment_status,
      shipping_status: shipMap[fulfillment_status] || "processing",
    });
  };

  const saveTrackingAndNotes = async () => {
    if (!selected) return;
    await patchOrder(selected.id, { tracking_number: trackingDraft.trim(), internal_notes: notesDraft });
  };

  const copyAddress = async () => {
    if (!selected) return;
    const t = formatAddress(selected.shipping_address);
    try {
      await navigator.clipboard.writeText(t || "");
    } catch {
      setOrdersError("Could not copy address. Select and copy manually.");
    }
  };

  const filteredOrders = useMemo(() => {
    if (statusFilter === "all") return orders;
    if (statusFilter === "paid_open") {
      return orders.filter(
        (o) => o.status === "paid" && o.fulfillment_status !== "completed" && o.fulfillment_status !== "cancelled_refunded",
      );
    }
    return orders.filter((o) => o.status === statusFilter || o.fulfillment_status === statusFilter);
  }, [orders, statusFilter]);

  if (!supabase) {
    return (
      <div className="min-h-screen bg-offwhite text-forest px-6 py-16 max-w-lg mx-auto">
        <h1 className="font-serif text-3xl">Admin</h1>
        <p className="mt-4 text-sm text-botanical/85">
          Supabase is not configured. Set <code className="text-xs bg-cream px-1 rounded">REACT_APP_SUPABASE_URL</code>{" "}
          and <code className="text-xs bg-cream px-1 rounded">REACT_APP_SUPABASE_ANON_KEY</code> for the frontend build,
          then add your user to <code className="text-xs bg-cream px-1 rounded">admin_users</code> in Supabase.
        </p>
        <Link to="/" className="inline-block mt-8 text-gold hover:text-goldhover text-sm">
          ← Back to site
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-offwhite flex items-center justify-center text-botanical text-sm">
        Loading…
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-offwhite text-forest px-6 py-16 max-w-md mx-auto">
        <h1 className="font-serif text-3xl">MossMatrix admin</h1>
        <p className="mt-2 text-sm text-botanical/80">Sign in with a Supabase Auth account listed in admin_users.</p>
        <form onSubmit={signIn} className="mt-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-botanical/70">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-cream bg-white px-4 py-3 text-sm"
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-botanical/70">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-xl border border-cream bg-white px-4 py-3 text-sm"
              autoComplete="current-password"
              required
            />
          </div>
          {authError && <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{authError}</p>}
          <button
            type="submit"
            className="w-full rounded-full bg-forest text-cream py-3 text-sm font-medium hover:bg-botanical transition"
          >
            Sign in
          </button>
        </form>
        <Link to="/" className="inline-block mt-10 text-gold hover:text-goldhover text-sm">
          ← Back to site
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-offwhite text-forest pb-24">
      <header className="border-b border-cream bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-2xl">MossMatrix admin</h1>
            <div className="flex rounded-full border border-cream bg-offwhite p-0.5">
              <button
                type="button"
                onClick={() => setAdminTab("orders")}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  adminTab === "orders" ? "bg-forest text-cream" : "text-botanical hover:text-forest"
                }`}
              >
                Orders
              </button>
              <button
                type="button"
                onClick={() => setAdminTab("community_notes")}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  adminTab === "community_notes" ? "bg-forest text-cream" : "text-botanical hover:text-forest"
                }`}
              >
                Community notes
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-botanical/70 truncate max-w-[12rem] sm:max-w-xs">{session.user.email}</span>
            {adminTab === "orders" ? (
              <button
                type="button"
                onClick={() => loadOrders()}
                className="text-xs uppercase tracking-widest px-3 py-2 rounded-full border border-forest/20 hover:bg-forest hover:text-cream transition"
              >
                Refresh
              </button>
            ) : (
              <button
                type="button"
                onClick={() => loadCommunityNotes()}
                className="text-xs uppercase tracking-widest px-3 py-2 rounded-full border border-forest/20 hover:bg-forest hover:text-cream transition"
              >
                Refresh
              </button>
            )}
            <button
              type="button"
              onClick={signOut}
              className="text-xs uppercase tracking-widest px-3 py-2 rounded-full bg-cream hover:bg-forest hover:text-cream transition"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {adminTab === "orders" ? (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {ordersError && (
            <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{ordersError}</p>
          )}
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-botanical/70 whitespace-nowrap">Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 min-w-0 rounded-xl border border-cream bg-white px-3 py-2 text-sm"
            >
              <option value="all">All orders</option>
              <option value="pending_payment">Pending payment</option>
              <option value="paid">Paid (any stage)</option>
              <option value="paid_open">Paid · not completed</option>
              <option value="new_order">Fulfillment: New order</option>
              <option value="preparing">Fulfillment: Preparing</option>
              <option value="refunded">Payment: Refunded</option>
            </select>
          </div>
          <div className="rounded-2xl border border-cream bg-white overflow-hidden">
            <div className="max-h-[70vh] overflow-y-auto">
              {filteredOrders.length === 0 ? (
                <p className="p-6 text-sm text-botanical/70">No orders match this filter.</p>
              ) : (
                filteredOrders.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setSelectedId(o.id)}
                    className={`w-full text-left px-4 py-3 border-b border-cream last:border-0 text-sm transition ${
                      selectedId === o.id ? "bg-gold/15" : "hover:bg-cream/60"
                    }`}
                  >
                    <div className="font-medium text-forest">{new Date(o.created_at).toLocaleString()}</div>
                    <div className="text-xs text-botanical/70 mt-1">
                      {o.status} · {o.customer_email || "—"}
                    </div>
                    <div className="text-xs mt-1">
                      £{Number(o.total_amount).toFixed(2)} · {o.delivery_option || "—"} · {o.fulfillment_status || o.shipping_status}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
          <Link to="/" className="inline-block text-sm text-gold hover:text-goldhover">
            ← Storefront
          </Link>
        </div>

        <div className="lg:col-span-3">
          {!selected ? (
            <p className="text-sm text-botanical/70">Select an order to view details and update fulfilment.</p>
          ) : (
            <div className="rounded-2xl border border-cream bg-white p-5 sm:p-6 space-y-6">
              <div className="flex flex-wrap gap-4 justify-between items-start">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-botanical/60">Order</div>
                  <div className="font-mono text-xs break-all mt-1">{selected.id}</div>
                  <div className="text-sm mt-3">
                    <span className="text-botanical/70">Stripe session:</span>{" "}
                    <span className="break-all">{selected.stripe_session_id || "—"}</span>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-botanical/60 block">Order stage</label>
                  <select
                    value={selected.status === "paid" ? selected.fulfillment_status || "new_order" : ""}
                    onChange={(e) => setFulfillment(selected.id, e.target.value)}
                    disabled={selected.status !== "paid"}
                    className="mt-1 rounded-xl border border-cream bg-offwhite px-3 py-2 text-sm min-w-[13rem]"
                  >
                    {selected.status !== "paid" ? <option value="">Awaiting payment</option> : null}
                    {FULFILLMENT.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFulfillment(selected.id, "preparing")}
                  disabled={selected.status !== "paid"}
                  className="text-xs rounded-full border border-forest/20 px-3 py-2 hover:bg-forest hover:text-cream disabled:opacity-40"
                >
                  Mark as Preparing
                </button>
                <button
                  type="button"
                  onClick={() => setFulfillment(selected.id, "ready_to_dispatch")}
                  disabled={selected.status !== "paid"}
                  className="text-xs rounded-full border border-forest/20 px-3 py-2 hover:bg-forest hover:text-cream disabled:opacity-40"
                >
                  Mark as Ready to Dispatch
                </button>
                <button
                  type="button"
                  onClick={() => setFulfillment(selected.id, "dispatched")}
                  disabled={selected.status !== "paid"}
                  className="text-xs rounded-full border border-forest/20 px-3 py-2 hover:bg-forest hover:text-cream disabled:opacity-40"
                >
                  Mark as Dispatched
                </button>
                <button
                  type="button"
                  onClick={copyAddress}
                  className="text-xs rounded-full border border-forest/20 px-3 py-2 hover:bg-cream"
                >
                  Copy customer address
                </button>
                <button
                  type="button"
                  onClick={() => openPackingSlip(selected)}
                  className="text-xs rounded-full border border-forest/20 px-3 py-2 hover:bg-cream"
                >
                  Print packing slip
                </button>
              </div>

              <div className="rounded-xl border border-cream bg-offwhite px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-botanical/60 mb-2">Courier tools (opens new tab)</div>
                <div className="flex flex-wrap gap-2">
                  {COURIERS.map((c) => (
                    <a
                      key={c.label}
                      href={c.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs rounded-full bg-white border border-cream px-3 py-1.5 hover:border-gold"
                    >
                      {c.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-botanical/70">Customer name</span>
                  <div className="font-medium">{selected.customer_name || "—"}</div>
                </div>
                <div>
                  <span className="text-botanical/70">Email</span>
                  <div className="font-medium break-all">{selected.customer_email || "—"}</div>
                </div>
                <div>
                  <span className="text-botanical/70">Phone</span>
                  <div className="font-medium">{selected.customer_phone || "—"}</div>
                </div>
                <div>
                  <span className="text-botanical/70">Delivery option</span>
                  <div className="font-medium">{selected.delivery_option === "priority" ? "Priority UK" : "Standard UK"}</div>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-botanical/70">Delivery address</span>
                  <div className="font-medium whitespace-pre-wrap mt-1">{formatAddress(selected.shipping_address) || "—"}</div>
                </div>
                <div>
                  <span className="text-botanical/70">Fulfilment status</span>
                  <div className="font-medium">{selected.fulfillment_status || "—"}</div>
                </div>
                <div>
                  <span className="text-botanical/70">Shipping status</span>
                  <div className="font-medium">{selected.shipping_status || "—"}</div>
                </div>
                <div>
                  <span className="text-botanical/70">Payment status</span>
                  <div className="font-medium">{selected.status}</div>
                </div>
                <div>
                  <span className="text-botanical/70">Order total</span>
                  <div className="font-medium">
                    £{Number(selected.total_amount).toFixed(2)} (delivery £{Number(selected.shipping_amount).toFixed(2)})
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-botanical/60">Tracking number</label>
                  <input
                    value={trackingDraft}
                    onChange={(e) => setTrackingDraft(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-cream bg-offwhite px-3 py-2 text-sm"
                    placeholder="Paste tracking when dispatched"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-botanical/60">Internal notes</label>
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={2}
                    className="mt-1 w-full rounded-xl border border-cream bg-offwhite px-3 py-2 text-sm resize-y min-h-[72px]"
                    placeholder="Staff notes (not shown to customer)"
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="button"
                    onClick={saveTrackingAndNotes}
                    className="text-xs rounded-full bg-forest text-cream px-4 py-2 hover:bg-botanical"
                  >
                    Save tracking & notes
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-botanical/60 mb-2">Products ordered</div>
                <ul className="space-y-3">
                  {(selected.order_items || []).map((li) => (
                    <li key={li.id} className="rounded-xl border border-cream px-4 py-3 text-sm">
                      <div className="font-serif text-lg">{li.product_name}</div>
                      <div className="text-xs text-botanical/75 mt-1 space-y-0.5">
                        <div>
                          Type: <strong className="text-forest">{li.product_type || "—"}</strong> · Packaging:{" "}
                          <strong className="text-forest">{li.packaging_type || "—"}</strong> · Base:{" "}
                          <strong className="text-forest">{li.base_type}</strong> · Size:{" "}
                          <strong className="text-forest">{li.size_ml} ml</strong>
                        </div>
                        <div>
                          SKU: {li.sku || "—"} · Custom: {li.is_custom ? "yes" : "no"} · Qty {li.quantity}
                        </div>
                        <div>Unit price £{Number(li.unit_price).toFixed(2)}</div>
                        <div className="font-mono break-all text-[11px] mt-1">Signature: {li.blend_signature}</div>
                        <div>Herbs: {Array.isArray(li.herbs) ? JSON.stringify(li.herbs) : String(li.herbs)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-4">
          <h2 className="font-serif text-xl text-forest">Community notes</h2>
          <p className="text-xs text-botanical/75 max-w-2xl leading-relaxed">
            Pending notes are hidden from the site until you approve them. If the list fails to load, run{" "}
            <code className="text-[11px] bg-cream px-1 rounded">supabase/migrations/20260215_ingredient_notes.sql</code> in the Supabase SQL editor.
          </p>
          {communityNotesError ? (
            <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{communityNotesError}</p>
          ) : null}
          {communityNotesLoading ? (
            <p className="text-sm text-botanical/70">Loading…</p>
          ) : communityNotes.length === 0 ? (
            <p className="text-sm text-botanical/70">No community notes yet.</p>
          ) : (
            <div className="space-y-4">
              {[...communityNotes]
                .sort((a, b) => {
                  const rank = (s) => (s === "pending" ? 0 : s === "approved" ? 1 : 2);
                  const d = rank(a.status) - rank(b.status);
                  if (d !== 0) return d;
                  return new Date(b.created_at) - new Date(a.created_at);
                })
                .map((note) => (
                  <div key={note.id} className="rounded-2xl border border-cream bg-white p-4 text-sm shadow-soft">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <span className="font-medium text-forest">{note.display_name}</span>
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-botanical/55">{note.status}</span>
                      </div>
                      <span className="text-[10px] text-botanical/50 tabular-nums">
                        {new Date(note.created_at).toLocaleString("en-GB")}
                      </span>
                    </div>
                    <div className="text-xs text-botanical/70 mt-1">
                      Ingredient: <strong className="text-forest">{note.ingredient_id}</strong>
                    </div>
                    <div className="text-xs break-all mt-1 text-botanical/80">
                      Email: <span className="text-forest">{note.email}</span>
                    </div>
                    {note.location ? (
                      <div className="text-xs text-botanical/70 mt-1">
                        Location: <span className="text-forest">{note.location}</span>
                      </div>
                    ) : null}
                    <p className="mt-2 text-forest leading-relaxed whitespace-pre-wrap">{note.comment}</p>
                    {note.moderation_reason ? (
                      <p className="mt-2 text-xs text-botanical/75 italic border-t border-cream pt-2">{note.moderation_reason}</p>
                    ) : null}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {note.status === "pending" || note.status === "approved" ? (
                        <button
                          type="button"
                          onClick={() => approveCommunityNote(note.id)}
                          disabled={note.status === "approved"}
                          className="text-xs rounded-full bg-forest text-cream px-3 py-1.5 hover:bg-botanical disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Approve
                        </button>
                      ) : null}
                      {note.status !== "rejected" ? (
                        <button
                          type="button"
                          onClick={() => rejectCommunityNote(note.id)}
                          className="text-xs rounded-full border border-forest/20 px-3 py-1.5 hover:bg-cream"
                        >
                          Reject
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => deleteCommunityNote(note.id)}
                        className="text-xs rounded-full border border-red-200 text-red-900 px-3 py-1.5 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
          <Link to="/" className="inline-block text-sm text-gold hover:text-goldhover">
            ← Storefront
          </Link>
        </div>
      )}
    </div>
  );
}
